import { useGetJobsiteConfigGeofencingQuery } from 'apollo/generated/client-operations';
import { Form } from 'components/form';
import { LoadingError } from 'components/loadingError';
import { JobsiteConfigTabProps } from 'containers/jobsiteConfiguration/types';
import { useJobsiteConfigTabForm } from 'containers/jobsiteConfiguration/helpers/useJobsiteConfigTabForm';
import { useFlags } from 'featureFlags';
import React from 'react';
import { getGraphQLErrorByCode } from 'utils/error';
import {
  type EditJobsiteConfigTabFormData,
  getDefaultValues,
  getFormInputs,
  getJobsiteUpdateInput,
} from './GeofencingTab.forms';

export function GeofencingTab(
  props: JobsiteConfigTabProps,
): React.ReactElement {
  const { jobsiteId, tab, onTabApiChange, onIsDirtyChange } = props;

  // The gateways editor is hidden while the site layout designer is on;
  // gateways still back mobile check-in, stations and swipe filters.
  const { siteLayoutUiEnabled } = useFlags();
  const showGateways = !siteLayoutUiEnabled;

  const {
    data: jobsiteConfigData,
    loading: jobsiteConfigLoading,
    error: jobsiteConfigError,
    refetch,
  } = useGetJobsiteConfigGeofencingQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: { jobsiteId },
  });

  const jobsite = jobsiteConfigData?.getJobsite;

  React.useEffect(
    () =>
      onTabApiChange({
        refetchData: refetch,
        loading: jobsiteConfigLoading,
        jobsiteName: jobsite?.name,
      }),
    [onTabApiChange, refetch, jobsiteConfigLoading, jobsite?.name],
  );

  const { formRef, onSubmit } =
    useJobsiteConfigTabForm<EditJobsiteConfigTabFormData>({
      onTabApiChange,
      onIsDirtyChange,
      tab,
      refetch,
      getInput: (data, dirtyFields) =>
        getJobsiteUpdateInput(jobsite, data, dirtyFields),
      onError: (error, formApi): boolean => {
        const userInputError = getGraphQLErrorByCode<{ field: string }>(
          error,
          'BAD_USER_INPUT',
        );
        // No match for a network error or another code: the toast reports it.
        if (
          !showGateways ||
          userInputError?.extensions?.field !== 'jobsiteGateways'
        )
          return false;
        formApi.setError('gateways.jobsiteGateways', {
          message: userInputError.message,
          shouldFocus: true,
        });
        return true;
      },
    });

  const { formInputs, defaultValues } = React.useMemo(() => {
    return {
      formInputs: getFormInputs({ jobsite, showGateways }),
      defaultValues: getDefaultValues({ jobsite, showGateways }),
    };
  }, [jobsite, showGateways]);

  if (!jobsite) {
    return <LoadingError loading />;
  }

  if (jobsiteConfigError) {
    return <LoadingError error={jobsiteConfigError} />;
  }

  return (
    <div className="odin-relative">
      {jobsiteConfigLoading && (
        <div className="table-backdrop">
          <LoadingError loading />
        </div>
      )}
      <Form
        ref={formRef}
        inputs={formInputs}
        onSubmit={onSubmit}
        onIsDirtyChange={onIsDirtyChange}
        defaultValues={defaultValues}
        className="odin-pr-3"
        inputsContainerClassName="odin-space-y-5 sm:odin-space-y-9 odin-divide-y odin-divide-gray-200 odin-pb-9"
        resetTrigger={jobsite}
      />
    </div>
  );
}
